import React, { useState, useEffect } from 'react'
import Namespace from 'src/constants/locale/Namespace'
import UaeMpciKeys from 'src/constants/locale/key/UaeMpci'
import { Grid, CardContent, Typography } from '@material-ui/core'
import { components, useTranslation } from 'cng-web-lib'
import makeValidationSchema from './MakeValidationSchema'
import CngSection from '../../../components/cngcomponents/CngSection'
import { useFormContext, useWatch } from 'react-hook-form'
import VesselInfoTable from './VesselInfoTable'
import VesselInfoDialog from './VesselInfoDialog'
import ContainerDetailsTable from './ContainerDetailsTable'
import ContainerDetailsDialog from './ContainerDetailsDialog'
import ConsignmentDetailsTable from './ConsignmentDetailsTable'
import ConsignmentDetailsDialog from './ConsignmentDetailsDialog'
import PartiesInfoTable from './PartiesInfoTable'
import PartiesInfoDialog from './PartiesInfoDialog'
import GoodsInfoTable from './GoodsInfoTable'
import GoodsInfoDialog from './GoodsInfoDialog'
import SplitGoodsTable from './SplitGoodsTable'
import SplitGoodsDialog from './SplitGoodsDialog'
import { SHIPMENT_TYPE_HBL, SHIPMENT_TYPE_MBL } from '../Common/constants/filingOptions'

const {
  form: {
    field: {
      CngTextField,
      CngDateField,
      CngCodeMasterAutocompleteField,
      CngSelectField
    },
  },
  CngGridItem,
} = components

const DEFAULT_INITIAL_VALUES = Object.freeze({
  id: null,
  version: null,
  filingOperator: "",
  uaeFFMpciPartyId: "",
  documentType: "",
  billNo: "",
  parentBillNo: "",
  originalBillNo: "",
  functionType: "",
  reasonForUpdatingFiling: "",
  processingIndicator: "",
  bolIssueDate: "",
  sobDate: "",
  placeOfBillIssueCountry: "",
  billIssueLocationName: "",
  freightPayableCountry: "",
  freightLocationName: "",
  shipmentType: "",
  shipmentDescription: "",
  totalFFBills: "",
  filingOperatorMpciPartyId: "",
  status: "",
  vesselInfo: [],
  containerDetails: [],
  consignmentDetails: [],
  partiesInfo: [],
  goodsInfo: [],
  splitGoods: []
})

const FORMIK_PROPS = {
  initialValues: { ...DEFAULT_INITIAL_VALUES },
  makeValidationSchema: makeValidationSchema
}

function Fields({ disabled, showNotification, shouldHideMap }) {
  const { setValue, getValues, watch } = useFormContext()
  const { translate } = useTranslation(Namespace.UAE_MPCI)
  const translatedTextsObject = makeTranslatedTextsObject()

  const [vesselInfoDialog, setVesselInfoDialog] = useState({ open: false, vesselInfo: null, isView: false })
  const [containerDetailsDialog, setContainerDetailsDialog] = useState({ open: false, containerDetails: null, isView: false })
  const [consignmentDetailsDialog, setConsignmentDetailsDialog] = useState({ open: false, consignmentDetails: null, isView: false })
  const [partiesInfoDialog, setPartiesInfoDialog] = useState({ open: false, partiesInfo: null, isView: false })
  const [goodsInfoDialog, setGoodsInfoDialog] = useState({ open: false, goodsInfo: null, isView: false })
  const [splitGoodsDialog, setSplitGoodsDialog] = useState({ open: false, splitGoods: null, isView: false })

  const vesselInfo = watch('vesselInfo')
  const containerDetails = watch('containerDetails')
  const consignmentDetails = watch('consignmentDetails')
  const partiesInfo = watch('partiesInfo')
  const goodsInfo = watch('goodsInfo')
  const splitGoods = watch('splitGoods')
  const documentType = watch('documentType')

  // Generic handlers for sub-entities
  function makeHandlers(fieldName) {
    return {
      add: (data) => {
        const list = [...getValues(fieldName), data].map((item, index) => ({ ...item, _id: index + 1 }))
        setValue(fieldName, list, { shouldValidate: true })
      },
      edit: (data) => {
        const list = [...getValues(fieldName)]
        const index = list.findIndex((item) => item.id ? item.id === data.id : item._id === data._id)
        list[index] = data
        setValue(fieldName, list, { shouldValidate: true })
      },
      delete: (data) => {
        const list = [...getValues(fieldName)].filter((item) => item.id ? item.id !== data.id : item._id !== data._id)
        setValue(fieldName, list, { shouldValidate: true })
      }
    }
  }

  const vesselHandlers = makeHandlers('vesselInfo')
  const containerHandlers = makeHandlers('containerDetails')
  const consignmentHandlers = makeHandlers('consignmentDetails')
  const partiesHandlers = makeHandlers('partiesInfo')
  const goodsHandlers = makeHandlers('goodsInfo')
  const splitGoodsHandlers = makeHandlers('splitGoods')

  function makeTranslatedTextsObject() {
    return {
      title: translate(Namespace.UAE_MPCI, UaeMpciKeys.TITLE),
      filingOperator: translate(Namespace.UAE_MPCI, UaeMpciKeys.FILING_OPERATOR),
      uaeFFMpciPartyId: translate(Namespace.UAE_MPCI, UaeMpciKeys.UAE_FF_MPCI_PARTY_ID),
      documentType: translate(Namespace.UAE_MPCI, UaeMpciKeys.DOCUMENT_TYPE),
      billNo: translate(Namespace.UAE_MPCI, UaeMpciKeys.BILL_NO),
      parentBillNo: translate(Namespace.UAE_MPCI, UaeMpciKeys.PARENT_BILL_NO),
      originalBillNo: translate(Namespace.UAE_MPCI, UaeMpciKeys.ORIGINAL_BILL_NO),
      functionType: translate(Namespace.UAE_MPCI, UaeMpciKeys.FUNCTION_TYPE),
      reasonForUpdatingFiling: translate(Namespace.UAE_MPCI, UaeMpciKeys.REASON_FOR_UPDATING_FILING),
      processingIndicator: translate(Namespace.UAE_MPCI, UaeMpciKeys.PROCESSING_INDICATOR),
      bolIssueDate: translate(Namespace.UAE_MPCI, UaeMpciKeys.BOL_ISSUE_DATE),
      sobDate: translate(Namespace.UAE_MPCI, UaeMpciKeys.SOB_DATE),
      placeOfBillIssueCountry: translate(Namespace.UAE_MPCI, UaeMpciKeys.PLACE_OF_BILL_ISSUE_COUNTRY),
      billIssueLocationName: translate(Namespace.UAE_MPCI, UaeMpciKeys.BILL_ISSUE_LOCATION_NAME),
      freightPayableCountry: translate(Namespace.UAE_MPCI, UaeMpciKeys.FREIGHT_PAYABLE_COUNTRY),
      freightLocationName: translate(Namespace.UAE_MPCI, UaeMpciKeys.FREIGHT_LOCATION_NAME),
      shipmentType: translate(Namespace.UAE_MPCI, UaeMpciKeys.SHIPMENT_TYPE),
      shipmentDescription: translate(Namespace.UAE_MPCI, UaeMpciKeys.SHIPMENT_DESCRIPTION),
      totalFFBills: translate(Namespace.UAE_MPCI, UaeMpciKeys.TOTAL_FF_BILLS),
      vesselInfoTitle: translate(Namespace.UAE_MPCI, UaeMpciKeys.VesselInfo.TITLE),
      containerDetailsTitle: translate(Namespace.UAE_MPCI, UaeMpciKeys.ContainerDetails.TITLE),
      consignmentDetailsTitle: translate(Namespace.UAE_MPCI, UaeMpciKeys.ConsignmentDetails.TITLE),
      partiesInfoTitle: translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.TITLE),
      goodsInfoTitle: translate(Namespace.UAE_MPCI, UaeMpciKeys.GoodsInfo.TITLE),
      splitGoodsTitle: translate(Namespace.UAE_MPCI, UaeMpciKeys.SplitGoods.TITLE),
    }
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <CngSection
          title={translatedTextsObject.title}
          subheader={
            <>
              <Typography color="error" component="span" variant="caption">*</Typography>
              <Typography color="textSecondary" component="span" variant="caption"> Mandatory fields</Typography>
            </>
          }
        >
          <CardContent>
            <Grid container spacing={1}>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngCodeMasterAutocompleteField
                  required
                  name="filingOperator"
                  label={translatedTextsObject.filingOperator}
                  disabled={disabled}
                  codeType='UAE_MPCI_FILING_OPERATOR'
                  size='small'
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField
                  required
                  name="uaeFFMpciPartyId"
                  label={translatedTextsObject.uaeFFMpciPartyId}
                  disabled={disabled}
                  size='small'
                  inputProps={{ maxLength: 7 }}
                  onChange={(e) => setValue("uaeFFMpciPartyId", e.target.value.toUpperCase(), { shouldValidate: true })}
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngCodeMasterAutocompleteField
                  required
                  name="documentType"
                  label={translatedTextsObject.documentType}
                  disabled={disabled}
                  codeType='UAE_MPCI_DOC_TYPE'
                  size='small'
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField
                  required
                  name="billNo"
                  label={translatedTextsObject.billNo}
                  disabled={disabled}
                  size='small'
                  inputProps={{ maxLength: 70 }}
                  onChange={(e) => setValue("billNo", e.target.value.toUpperCase(), { shouldValidate: true })}
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField
                  required
                  name="parentBillNo"
                  label={translatedTextsObject.parentBillNo}
                  disabled={disabled}
                  size='small'
                  inputProps={{ maxLength: 70 }}
                  onChange={(e) => setValue("parentBillNo", e.target.value.toUpperCase(), { shouldValidate: true })}
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField
                  name="originalBillNo"
                  label={translatedTextsObject.originalBillNo}
                  disabled={disabled}
                  size='small'
                  inputProps={{ maxLength: 70 }}
                  onChange={(e) => setValue("originalBillNo", e.target.value.toUpperCase(), { shouldValidate: true })}
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngCodeMasterAutocompleteField
                  required
                  name="functionType"
                  label={translatedTextsObject.functionType}
                  disabled={disabled}
                  codeType='UAE_MPCI_FUNCTION_TYPE'
                  size='small'
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngCodeMasterAutocompleteField
                  required
                  name="processingIndicator"
                  label={translatedTextsObject.processingIndicator}
                  disabled={disabled}
                  codeType='UAE_MPCI_PROCESSING_INDICATOR'
                  size='small'
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngSelectField
                  required
                  name="shipmentType"
                  label={translatedTextsObject.shipmentType}
                  disabled={disabled}
                  size='small'
                  items={['704', '705'].includes(documentType) ? SHIPMENT_TYPE_MBL : SHIPMENT_TYPE_HBL}
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField
                  required
                  name="totalFFBills"
                  label={translatedTextsObject.totalFFBills}
                  disabled={disabled}
                  size='small'
                  inputProps={{ maxLength: 18 }}
                />
              </CngGridItem>
                            <CngGridItem xs={12} sm={6} lg={4}>
                <CngDateField
                  name="bolIssueDate"
                  label={translatedTextsObject.bolIssueDate}
                  disabled={disabled}
                  size='small'
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngDateField
                  name="sobDate"
                  label={translatedTextsObject.sobDate}
                  disabled={disabled}
                  size='small'
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField
                  name="placeOfBillIssueCountry"
                  label={translatedTextsObject.placeOfBillIssueCountry}
                  disabled={disabled}
                  size='small'
                  inputProps={{ maxLength: 2 }}
                  onChange={(e) => setValue("placeOfBillIssueCountry", e.target.value.toUpperCase(), { shouldValidate: true })}
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField
                  name="billIssueLocationName"
                  label={translatedTextsObject.billIssueLocationName}
                  disabled={disabled}
                  size='small'
                  inputProps={{ maxLength: 256 }}
                  onChange={(e) => setValue("billIssueLocationName", e.target.value.toUpperCase(), { shouldValidate: true })}
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField
                  name="freightPayableCountry"
                  label={translatedTextsObject.freightPayableCountry}
                  disabled={disabled}
                  size='small'
                  inputProps={{ maxLength: 2 }}
                  onChange={(e) => setValue("freightPayableCountry", e.target.value.toUpperCase(), { shouldValidate: true })}
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField
                  name="freightLocationName"
                  label={translatedTextsObject.freightLocationName}
                  disabled={disabled}
                  size='small'
                  inputProps={{ maxLength: 256 }}
                  onChange={(e) => setValue("freightLocationName", e.target.value.toUpperCase(), { shouldValidate: true })}
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={6} lg={4}>
                <CngTextField
                  name="shipmentDescription"
                  label={translatedTextsObject.shipmentDescription}
                  disabled={disabled}
                  size='small'
                  inputProps={{ maxLength: 35 }}
                  onChange={(e) => setValue("shipmentDescription", e.target.value.toUpperCase(), { shouldValidate: true })}
                />
              </CngGridItem>
              <CngGridItem xs={12} sm={12} lg={12}>
                <CngTextField
                  name="reasonForUpdatingFiling"
                  label={translatedTextsObject.reasonForUpdatingFiling}
                  disabled={disabled}
                  size='small'
                  inputProps={{ maxLength: 512 }}
                  multiline
                  rows={2}
                  onChange={(e) => setValue("reasonForUpdatingFiling", e.target.value.toUpperCase(), { shouldValidate: true })}
                />
              </CngGridItem>
            </Grid>
          </CardContent>
        </CngSection>
      </Grid>

      {/* Vessel Info Section */}
      <Grid item xs={12}>
        <CngSection title={translatedTextsObject.vesselInfoTitle}>
          <VesselInfoTable
            data={vesselInfo}
            onAddVesselInfo={() => setVesselInfoDialog({ open: true, vesselInfo: null, isView: false })}
            onEditVesselInfo={(item) => setVesselInfoDialog({ open: true, vesselInfo: item, isView: false })}
            onDeleteVesselInfo={vesselHandlers.delete}
            showNotification={showNotification}
          />
          <VesselInfoDialog
            vesselInfo={vesselInfoDialog.vesselInfo}
            isView={vesselInfoDialog.isView}
            onAddVesselInfo={vesselHandlers.add}
            onEditVesselInfo={vesselHandlers.edit}
            open={vesselInfoDialog.open}
            onClose={() => setVesselInfoDialog({ open: false, vesselInfo: null, isView: false })}
            showNotification={showNotification}
          />
        </CngSection>
      </Grid>

      {/* Container Details Section */}
      <Grid item xs={12}>
        <CngSection title={translatedTextsObject.containerDetailsTitle}>
          <ContainerDetailsTable
            data={containerDetails}
            onAddContainerDetails={() => setContainerDetailsDialog({ open: true, containerDetails: null, isView: false })}
            onEditContainerDetails={(item) => setContainerDetailsDialog({ open: true, containerDetails: item, isView: false })}
            onDeleteContainerDetails={containerHandlers.delete}
            showNotification={showNotification}
          />
          <ContainerDetailsDialog
            containerDetails={containerDetailsDialog.containerDetails}
            isView={containerDetailsDialog.isView}
            onAddContainerDetails={containerHandlers.add}
            onEditContainerDetails={containerHandlers.edit}
            open={containerDetailsDialog.open}
            onClose={() => setContainerDetailsDialog({ open: false, containerDetails: null, isView: false })}
            showNotification={showNotification}
          />
        </CngSection>
      </Grid>

      {/* Consignment Details Section */}
      <Grid item xs={12}>
        <CngSection title={translatedTextsObject.consignmentDetailsTitle}>
          <ConsignmentDetailsTable
            data={consignmentDetails}
            onAddConsignmentDetails={() => setConsignmentDetailsDialog({ open: true, consignmentDetails: null, isView: false })}
            onEditConsignmentDetails={(item) => setConsignmentDetailsDialog({ open: true, consignmentDetails: item, isView: false })}
            onDeleteConsignmentDetails={consignmentHandlers.delete}
            showNotification={showNotification}
          />
          <ConsignmentDetailsDialog
            consignmentDetails={consignmentDetailsDialog.consignmentDetails}
            isView={consignmentDetailsDialog.isView}
            onAddConsignmentDetails={consignmentHandlers.add}
            onEditConsignmentDetails={consignmentHandlers.edit}
            open={consignmentDetailsDialog.open}
            onClose={() => setConsignmentDetailsDialog({ open: false, consignmentDetails: null, isView: false })}
            showNotification={showNotification}
          />
        </CngSection>
      </Grid>

      {/* Parties Info Section */}
      <Grid item xs={12}>
        <CngSection title={translatedTextsObject.partiesInfoTitle}>
          <PartiesInfoTable
            data={partiesInfo}
            onAddPartiesInfo={() => setPartiesInfoDialog({ open: true, partiesInfo: null, isView: false })}
            onEditPartiesInfo={(item) => setPartiesInfoDialog({ open: true, partiesInfo: item, isView: false })}
            onDeletePartiesInfo={partiesHandlers.delete}
            showNotification={showNotification}
          />
          <PartiesInfoDialog
            partiesInfo={partiesInfoDialog.partiesInfo}
            isView={partiesInfoDialog.isView}
            onAddPartiesInfo={partiesHandlers.add}
            onEditPartiesInfo={partiesHandlers.edit}
            open={partiesInfoDialog.open}
            onClose={() => setPartiesInfoDialog({ open: false, partiesInfo: null, isView: false })}
            showNotification={showNotification}
          />
        </CngSection>
      </Grid>

      {/* Goods Info Section */}
      <Grid item xs={12}>
        <CngSection title={translatedTextsObject.goodsInfoTitle}>
          <GoodsInfoTable
            data={goodsInfo}
            onAddGoodsInfo={() => setGoodsInfoDialog({ open: true, goodsInfo: null, isView: false })}
            onEditGoodsInfo={(item) => setGoodsInfoDialog({ open: true, goodsInfo: item, isView: false })}
            onDeleteGoodsInfo={goodsHandlers.delete}
            showNotification={showNotification}
          />
          {goodsInfoDialog.open && (
            <GoodsInfoDialog
              goodsInfo={goodsInfoDialog.goodsInfo}
              isView={goodsInfoDialog.isView}
              onAddGoodsInfo={goodsHandlers.add}
              onEditGoodsInfo={goodsHandlers.edit}
              open={goodsInfoDialog.open}
              onClose={() => setGoodsInfoDialog({ open: false, goodsInfo: null, isView: false })}
              showNotification={showNotification}
              documentType={documentType}
            />
          )}
        </CngSection>
      </Grid>

      {/* Split Goods Section */}
      <Grid item xs={12}>
        <CngSection title={translatedTextsObject.splitGoodsTitle}>
          <SplitGoodsTable
            data={splitGoods}
            onAddSplitGoods={() => setSplitGoodsDialog({ open: true, splitGoods: null, isView: false })}
            onEditSplitGoods={(item) => setSplitGoodsDialog({ open: true, splitGoods: item, isView: false })}
            onDeleteSplitGoods={splitGoodsHandlers.delete}
            showNotification={showNotification}
          />
          <SplitGoodsDialog
            splitGoods={splitGoodsDialog.splitGoods}
            isView={splitGoodsDialog.isView}
            onAddSplitGoods={splitGoodsHandlers.add}
            onEditSplitGoods={splitGoodsHandlers.edit}
            open={splitGoodsDialog.open}
            onClose={() => setSplitGoodsDialog({ open: false, splitGoods: null, isView: false })}
            showNotification={showNotification}
          />
        </CngSection>
      </Grid>
    </Grid>
  )
}

function toClientDataFormat(serverData) {
  let localData = serverData
  return localData
}

function toServerDataFormat(localData) {
  return localData
}

const FormProperties = Object.freeze({
  formikProps: FORMIK_PROPS,
  Fields: Fields,
  toClientDataFormat: toClientDataFormat,
  toServerDataFormat: toServerDataFormat
})

export default FormProperties
